import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Activity, AlertTriangle, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { intelligenceApi, type DataQualityStatus } from "@/api/intelligence";
import { superAdminApi } from "@/api/superAdmin";
import { defaultFinancialReportHospitalId } from "@/constants/pnlDefaultScope";
import { intelligenceReportHospitalId } from "@/hooks/useChainReportHospitalScope";
import { IntelligenceSubNav } from "@/components/intelligence/IntelligenceSubNav";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

type Props = { basePath?: string };

function statusBadgeClass(status: DataQualityStatus): string {
	switch (status) {
		case "HEALTHY":
			return "bg-emerald-100 text-emerald-900";
		case "WARNING":
			return "bg-amber-100 text-amber-900";
		case "ERROR":
			return "bg-red-100 text-red-900";
		default:
			return "bg-slate-100 text-slate-800";
	}
}

function formatWhen(iso: string | null): string {
	if (!iso) return "Never";
	try {
		return new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
	} catch {
		return iso;
	}
}

export default function IntelligenceDataQualityPage({ basePath }: Props) {
	const navBase = basePath ?? "/hospital-admin";
	const { user, activeHospitalId, isChainAdmin } = useAuth();
	const isSuperAdmin = user?.role === "SUPER_ADMIN";
	const isInvestor = user?.role === "INVESTOR";
	const investorHasChainScope = isInvestor && (user?.hospitals?.length ?? 0) > 1;
	const isChainScoped = isChainAdmin || investorHasChainScope;
	const showHospitalFilter = isSuperAdmin || isChainScoped;
	const chainHospitals = user?.hospitals ?? [];

	const defaultHospitalId = useMemo(
		() =>
			defaultFinancialReportHospitalId({
				isSuperAdmin,
				isChainScoped,
				activeHospitalId,
				fallbackHospitalId: chainHospitals[0]?.id ?? null,
			}),
		[isSuperAdmin, isChainScoped, activeHospitalId, chainHospitals]
	);

	const [selectedHospitalId, setSelectedHospitalId] = useState(defaultHospitalId ?? "");

	const { data: superHospitals } = useQuery({
		queryKey: ["super-admin-hospitals-dq"],
		queryFn: () => superAdminApi.getHospitals(),
		enabled: isSuperAdmin,
	});

	const hospitalOptions = useMemo(() => {
		if (isSuperAdmin && superHospitals?.length) {
			return [
				{ id: "all", name: "All hospitals in scope" },
				...superHospitals.map((h) => ({ id: h.id, name: h.name })),
			];
		}
		if (isChainScoped && chainHospitals.length) {
			return [
				{ id: "all", name: "All hospitals in scope" },
				...chainHospitals.map((h) => ({ id: h.id, name: h.name })),
			];
		}
		return selectedHospitalId ? [{ id: selectedHospitalId, name: "Current hospital" }] : [];
	}, [isSuperAdmin, superHospitals, isChainScoped, chainHospitals, selectedHospitalId]);

	const reportHospitalId = intelligenceReportHospitalId({
		showHospitalFilter,
		selectedHospitalId,
		isChainScoped,
	});

	const { data, isLoading, isError, error } = useQuery({
		queryKey: ["intelligence-data-quality", reportHospitalId ?? "all"],
		queryFn: () => intelligenceApi.getDataQuality({ hospitalId: reportHospitalId }),
	});

	return (
		<div className="space-y-6 pb-10 p-4 md:p-6 max-w-6xl mx-auto">
			<IntelligenceSubNav basePath={navBase} />

			<div>
				<div className="flex items-center gap-2 text-indigo-700">
					<Activity className="h-6 w-6" />
					<h1 className="text-2xl font-bold tracking-tight text-slate-900">Data Quality</h1>
				</div>
				<p className="text-sm text-muted-foreground mt-1">
					Aggregation freshness, detection runs, rollup coverage, and configuration anomalies.
				</p>
			</div>

			{hospitalOptions.length > 1 && (
				<div className="flex flex-wrap items-center gap-2">
					<label htmlFor="dq-hospital" className="text-sm text-muted-foreground">
						Hospital
					</label>
					<select
						id="dq-hospital"
						className="border rounded-md px-3 py-1.5 text-sm"
						value={selectedHospitalId || "all"}
						onChange={(e) => setSelectedHospitalId(e.target.value)}
					>
						{hospitalOptions.map((h) => (
							<option key={h.id} value={h.id}>
								{h.name}
							</option>
						))}
					</select>
				</div>
			)}

			{isLoading && (
				<div className="flex items-center gap-2 py-12 text-muted-foreground">
					<Loader2 className="h-5 w-5 animate-spin" /> Loading data quality…
				</div>
			)}

			{isError && (
				<Card className="border-destructive/50">
					<CardContent className="pt-4 text-sm text-destructive flex gap-2">
						<AlertTriangle className="h-4 w-4 shrink-0" />
						{(error as Error)?.message ?? "Failed to load data quality"}
					</CardContent>
				</Card>
			)}

			{data?.chainSummary && (
				<Card>
					<CardHeader>
						<CardTitle className="text-base">Chain summary</CardTitle>
					</CardHeader>
					<CardContent className="text-sm space-y-1">
						<p>{data.chainSummary.hospitalCount} hospitals in scope</p>
						<p>
							Aggregation: {data.chainSummary.aggregation.HEALTHY} healthy ·{" "}
							{data.chainSummary.aggregation.WARNING} warning ·{" "}
							{data.chainSummary.aggregation.STALE} stale ·{" "}
							{data.chainSummary.aggregation.ERROR} error
						</p>
						<p>
							Detection: {data.chainSummary.detection.HEALTHY} healthy ·{" "}
							{data.chainSummary.detection.STALE} stale · {data.chainSummary.detection.ERROR}{" "}
							error
						</p>
						<p>Missing daily facts (30d total): {data.chainSummary.totalMissingDailyFacts}</p>
					</CardContent>
				</Card>
			)}

			{data?.hospitals.map((row) => (
				<Card key={row.hospitalId}>
					<CardHeader>
						<CardTitle className="text-base flex flex-wrap items-center gap-2">
							{row.hospitalName}
							<span
								className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusBadgeClass(row.aggregation.status)}`}
							>
								Aggregation: {row.aggregation.status}
							</span>
							<span
								className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusBadgeClass(row.detection.status)}`}
							>
								Detection: {row.detection.status}
							</span>
						</CardTitle>
					</CardHeader>
					<CardContent className="text-sm space-y-3">
						<div className="grid sm:grid-cols-2 gap-3">
							<div>
								<p className="font-medium text-slate-800">Daily aggregation</p>
								<p className="text-muted-foreground">
									Last success: {formatWhen(row.aggregation.lastSuccessAt)}
								</p>
								<p className="text-muted-foreground">
									Missing rollup days (last 30): {row.aggregation.missingDailyFacts}
								</p>
							</div>
							<div>
								<p className="font-medium text-slate-800">Detection run</p>
								<p className="text-muted-foreground">
									Last success: {formatWhen(row.detection.lastSuccessAt)}
								</p>
							</div>
						</div>
						<div>
							<p className="font-medium text-slate-800">KPI catalog availability</p>
							<p className="text-muted-foreground">
								{row.kpiAvailability.implemented} available · {row.kpiAvailability.limited}{" "}
								with limitations · {row.kpiAvailability.unavailable} unavailable
							</p>
						</div>
						{row.configurationAnomalies.length > 0 && (
							<div>
								<p className="font-medium text-amber-900">Configuration anomalies</p>
								<ul className="list-disc pl-5 text-amber-900/90 space-y-1">
									{row.configurationAnomalies.map((a) => (
										<li key={a}>{a}</li>
									))}
								</ul>
							</div>
						)}
					</CardContent>
				</Card>
			))}

			{data && (
				<p className="text-xs text-muted-foreground">
					Generated {formatWhen(data.generatedAt)} · Thresholds: healthy ≤26h, warning ≤48h
					(aggregation only)
				</p>
			)}
		</div>
	);
}
